import React, { useEffect, useRef, useState } from 'react';
import { useReveal, GlowDivider, StatCard } from '../components/Shared';

/* ── Particle Canvas ─────────────────────────────── */
function useCanvas(particleRef) {
  useEffect(() => {
    const pc  = particleRef.current;
    const pct = pc.getContext('2d');
    let PW, PH, particles = [];
    const pResize = () => { PW = pc.width = pc.offsetWidth; PH = pc.height = pc.offsetHeight; };
    pResize();
    window.addEventListener('resize', pResize);
    class P {
      constructor() { this.x=Math.random()*PW; this.y=Math.random()*PH; this.vx=(Math.random()-.5)*.38; this.vy=(Math.random()-.5)*.38; this.r=Math.random()*1.5+.3; this.a=Math.random()*.4+.08; this.c=Math.random()>.55?'#00ffff':'#8b5cf6'; }
      update() { this.x+=this.vx; this.y+=this.vy; if(this.x<0||this.x>PW)this.vx*=-1; if(this.y<0||this.y>PH)this.vy*=-1; }
      draw() { pct.save(); pct.globalAlpha=this.a; pct.fillStyle=this.c; pct.shadowBlur=10; pct.shadowColor=this.c; pct.beginPath(); pct.arc(this.x,this.y,this.r,0,Math.PI*2); pct.fill(); pct.restore(); }
    }
    for(let i=0;i<110;i++) particles.push(new P());
    let pRaf;
    const pLoop = () => {
      pct.clearRect(0,0,PW,PH);
      for(let i=0;i<particles.length;i++){
        for(let j=i+1;j<particles.length;j++){
          const dx=particles[i].x-particles[j].x, dy=particles[i].y-particles[j].y, d=Math.sqrt(dx*dx+dy*dy);
          if(d<110){ pct.save(); pct.globalAlpha=(1-d/110)*.1; pct.strokeStyle='#00ffff'; pct.lineWidth=.5; pct.beginPath(); pct.moveTo(particles[i].x,particles[i].y); pct.lineTo(particles[j].x,particles[j].y); pct.stroke(); pct.restore(); }
        }
        particles[i].update(); particles[i].draw();
      }
      pRaf = requestAnimationFrame(pLoop);
    };
    pLoop();
    return () => { cancelAnimationFrame(pRaf); window.removeEventListener('resize',pResize); };
  }, []);
}

/* ── Hero Cards data ───────────────────────────── */
const HERO_CARDS = [
  { title:'Modeling',    desc:'Multi-scale simulations bridging length and time scales', icon:<path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/> },
  { title:'Validation',  desc:'AI-driven search to identify promising candidates',      icon:<path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/> },
  { title:'Scale-Up',    desc:'Robotic fabrication for real-world testing',             icon:<path d="M13 10V3L4 14h7v7l9-11h-7z"/> },
];

const SERVICES = [
  { tag:'Phase 01', title:'Discovery',    color:'teal',   desc:'AI-driven search and screening to rapidly identify promising materials from vast design spaces.', features:['Graph neural networks','Bayesian optimization','High-throughput screening'], href:'/rd' },
  { tag:'Phase 02', title:'Scale-Up',     color:'purple', desc:'Robot-assisted fabrication and validation for manufacturing readiness with closed-loop systems.', features:['Robotic synthesis pods','Closed-loop feedback','TRL 4→7 acceleration'], href:'/rd', featured:true },
  { tag:'Our People', title:'Expert Team', color:'pink',  desc:'PhD scientists and engineers with deep experience in synthesis, characterization, and automation.', features:['Multidisciplinary PhDs','Industry partnerships','Global collaborators'], href:'/team' },
];

const colorMap = { teal:'#00ffff', purple:'#a78bfa', pink:'#f9a8d4' };
const bgMap    = { teal:'rgba(0,255,255,0.08)', purple:'rgba(139,92,246,0.08)', pink:'rgba(244,114,182,0.07)' };
const borderMap= { teal:'rgba(0,255,255,0.18)', purple:'rgba(139,92,246,0.22)', pink:'rgba(244,114,182,0.18)' };

export default function Home() {
  const particleRef = useRef(null);
  useCanvas(particleRef);
  useReveal();

  return (
    <>
      {/* ── HERO ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center overflow-hidden"
        style={{ padding:'140px 28px 100px', background:'radial-gradient(ellipse at 25% 15%,rgba(0,255,255,0.07) 0%,transparent 55%),radial-gradient(ellipse at 75% 85%,rgba(139,92,246,0.06) 0%,transparent 55%),var(--bg-0)' }}>

        <canvas ref={particleRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{zIndex:1}} />

        {/* Blueprint grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage:'linear-gradient(rgba(0,255,255,0.022) 1px,transparent 1px),linear-gradient(90deg,rgba(0,255,255,0.022) 1px,transparent 1px)', backgroundSize:'80px 80px', maskImage:'radial-gradient(ellipse at center,black 25%,transparent 72%)', animation:'gridDrift 22s linear infinite', zIndex:1 }} />

        {/* Orbs */}
        <div className="absolute rounded-full pointer-events-none" style={{ width:520,height:520,top:-100,left:-130,background:'radial-gradient(circle,rgba(0,255,255,0.13) 0%,transparent 70%)',filter:'blur(90px)',animation:'orbFloat1 14s ease-in-out infinite',zIndex:0 }} />
        <div className="absolute rounded-full pointer-events-none" style={{ width:420,height:420,bottom:-80,right:-90,background:'radial-gradient(circle,rgba(139,92,246,0.11) 0%,transparent 70%)',filter:'blur(90px)',animation:'orbFloat2 17s ease-in-out infinite',zIndex:0 }} />

        {/* Orbit rings */}
        <div className="absolute pointer-events-none" style={{ width:640,height:640,top:'50%',left:'50%',transform:'translate(-50%,-50%)',zIndex:0 }}>
          {[{size:0,dur:'22s'},{size:70,dur:'30s',rev:true},{size:140,dur:'18s'}].map((r,i)=>(
            <div key={i} className="absolute inset-0 rounded-full" style={{ inset:r.size, border:'1px solid rgba(0,255,255,0.055)', animation:`orbitSpin ${r.dur} linear infinite ${r.rev?'reverse':''}` }}>
              {i===0 && <div className="absolute top-0 left-1/2 w-[6px] h-[6px] rounded-full" style={{ background:'#00ffff',boxShadow:'0 0 12px #00ffff',transform:'translate(-50%,-50%)' }} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-[2] max-w-[880px]">
          <div className="inline-flex items-center gap-[10px] px-[22px] py-[8px] rounded-full font-mono text-[0.68rem] tracking-[3px] uppercase mb-9 relative overflow-hidden"
            style={{ color:'#00ffff', background:'rgba(0,255,255,0.06)', border:'1px solid rgba(0,255,255,0.15)', boxShadow:'0 0 24px rgba(0,255,255,0.05)', animation:'fadeUp .8s var(--ease-out) .1s both' }}>
            <span className="block w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ background:'#00ffff', animation:'pingSlow 2s ease-in-out infinite' }} />
            Autonomous Materials Innovation
          </div>

          <h1 className="font-display font-extrabold mb-7" style={{ fontSize:'clamp(3.2rem,8.5vw,6.5rem)', lineHeight:1.02, letterSpacing:'-0.048em', animation:'fadeUp .9s var(--ease-out) .25s both' }}>
            Discovery to Scale
            <br/>
            <span className="text-gradient" style={{ filter:'drop-shadow(0 0 40px rgba(0,255,255,0.15))' }}>(D2S) Lab</span>
          </h1>

          <p className="font-light mx-auto mb-[52px]" style={{ fontFamily:'var(--font-body)', fontSize:'clamp(1rem,1.9vw,1.22rem)', color:'var(--text-2)', maxWidth:560, lineHeight:1.8, animation:'fadeUp .9s var(--ease-out) .4s both' }}>
            Autonomous Discovery and Validation of Manufacturing Scale-up for Advanced Materials
          </p>

          <div className="flex gap-4 justify-center flex-wrap" style={{ animation:'fadeUp .9s var(--ease-out) .55s both' }}>
            <a href="/about" className="btn btn--primary">Explore Our Work</a>
            <a href="/contact" className="btn btn--glass">Get In Touch</a>
          </div>
        </div>

        {/* Feature cards */}
        <div className="relative z-[2] grid grid-cols-1 md:grid-cols-3 gap-5 max-w-[860px] w-full mt-20" style={{ animation:'fadeUp 1s var(--ease-out) .7s both' }}>
          {HERO_CARDS.map(({ title, desc, icon }) => (
            <div key={title} className="glass-card text-center" style={{ padding:'34px 28px' }}>
              <div className="relative w-12 h-12 mx-auto mb-[18px] flex items-center justify-center" style={{ color:'#00ffff' }}>
                <div className="absolute inset-[-10px] rounded-full" style={{ background:'radial-gradient(circle,rgba(0,255,255,0.09) 0%,transparent 70%)', animation:'iconGlow 3s ease-in-out infinite' }} />
                <svg className="relative z-[1] w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">{icon}</svg>
              </div>
              <h3 className="font-display font-semibold text-[1.05rem] tracking-[-0.02em] mb-[10px]">{title}</h3>
              <p className="text-[0.82rem] font-light leading-relaxed" style={{ color:'var(--text-2)' }}>{desc}</p>
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <a href="#mission" className="absolute bottom-9 left-1/2 flex flex-col items-center gap-2 no-underline" style={{ transform:'translateX(-50%)', animation:'fadeUp 1s var(--ease-out) 1.2s both' }}>
          <span className="font-mono text-[0.6rem] tracking-[4px] uppercase" style={{ color:'var(--text-3)' }}>Scroll</span>
          <div className="flex justify-center rounded-[12px] pt-[7px]" style={{ width:22,height:36,border:'1.5px solid rgba(0,255,255,0.28)',animation:'scrollBounce 2.5s ease-in-out infinite' }}>
            <div className="w-[3px] h-[7px] rounded-full" style={{ background:'#00ffff',animation:'scrollWheel 2.5s ease-in-out infinite' }} />
          </div>
        </a>
      </section>

      <GlowDivider />

      {/* ── MISSION ── */}
      <section className="section section-alt" id="mission">
        <div className="container">
          <div className="text-center mb-16 reveal">
            <span className="section-tag">Who We Are</span>
            <h2 className="section-title">Driving the Future of <span className="text-gradient">Materials Science</span></h2>
            <p className="text-sm font-light leading-relaxed mx-auto max-w-[500px]" style={{ color:'var(--text-2)' }}>
              We bridge the gap between laboratory discovery and industrial scale through AI, robotics, and multi-scale modeling.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { label:'Mission', color:'teal', title:'Our Mission', body:'To accelerate materials discovery and scale-up by integrating modeling, AI, robotics, testing, and manufacturing readiness into one seamless innovation pipeline.', items:['AI-driven candidate screening','Automated synthesis & testing','Closed-loop optimization'] },
              { label:'Vision',  color:'purple', title:'Our Vision',  body:'To shape a future where advanced materials move rapidly from discovery to deployment, enabling breakthrough technologies and scalable real-world impact.',  items:['10× faster time to market','Manufacturing-ready outputs','Global research partnerships'] },
            ].map(({ label, color, title, body, items }) => (
              <div key={title} className={`glass-card reveal-${color==='teal'?'left':'right'}`} style={{ padding:'44px 38px', position:'relative' }}>
                <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-[18px]"
                  style={{ background:`linear-gradient(90deg,transparent,${colorMap[color]},transparent)`, boxShadow:`0 0 20px ${bgMap[color]}` }} />
                <div className="inline-block font-mono text-[0.65rem] tracking-[3px] uppercase rounded-full px-3 py-1 mb-3"
                  style={{ color:colorMap[color], background:bgMap[color], border:`1px solid ${borderMap[color]}` }}>{label}</div>
                <h3 className="font-display font-bold text-[1.5rem] tracking-[-0.03em] mb-3">{title}</h3>
                <p className="text-sm font-light leading-[1.75] mb-5" style={{ color:'var(--text-2)' }}>{body}</p>
                <ul className="flex flex-col gap-2">
                  {items.map(i => (
                    <li key={i} className="flex items-center gap-[10px] text-sm" style={{ color:'var(--text-2)' }}>
                      <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background:colorMap[color], boxShadow:`0 0 8px ${colorMap[color]}50` }} />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* ── STATS ── */}
      <section className="section section-dark">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[{n:'10',s:'x',l:'Faster Discovery'},{n:'100',s:'%',l:'Integrated Pipeline'},{n:'5',s:'+',l:'Scale Disciplines'},{n:'24',s:'/7',l:'Autonomous Pods'}].map(({n,s,l})=>(
              <StatCard key={l} num={n} suffix={s} label={l} />
            ))}
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* ── SERVICES ── */}
      <section className="section" id="services">
        <div className="container">
          <div className="text-center mb-16 reveal">
            <span className="section-tag">What We Do</span>
            <h2 className="section-title">Our <span className="text-gradient">Services</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {SERVICES.map(({ tag, title, color, desc, features, href, featured }) => (
              <div key={title} className="glass-card reveal" style={{ padding:'40px 34px', ...(featured?{ background:'linear-gradient(160deg,rgba(20,14,60,.9) 0%,rgba(16,10,50,.8) 100%)', borderColor:'rgba(139,92,246,0.25)', boxShadow:'0 0 60px rgba(139,92,246,0.1)' }:{}) }}>
                {featured && <div className="inline-block font-mono text-[0.62rem] tracking-[2px] uppercase rounded-full px-3 py-1 mb-5" style={{ color:'#a78bfa', background:'rgba(139,92,246,0.12)', border:'1px solid rgba(139,92,246,0.22)' }}>Most Requested</div>}
                <div className="flex items-center justify-between mb-6">
                  <div className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center transition-transform duration-300 hover:scale-110 hover:rotate-[-5deg]"
                    style={{ background:bgMap[color], color:colorMap[color], border:`1px solid ${borderMap[color]}` }}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      {color==='teal'   && <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>}
                      {color==='purple' && <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>}
                      {color==='pink'   && <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-8.038 0l-2.387.477a2 2 0 00-1.022.547M12 12a4 4 0 100-8 4 4 0 000 8z"/>}
                    </svg>
                  </div>
                  <span className="font-mono text-[0.62rem] tracking-[2px] uppercase rounded-full px-2.5 py-1" style={{ color:colorMap[color], background:bgMap[color], border:`1px solid ${borderMap[color]}` }}>{tag}</span>
                </div>
                <h3 className="font-display font-bold text-[1.5rem] tracking-[-0.03em] mb-3">{title}</h3>
                <p className="text-[0.88rem] font-light leading-[1.72] mb-5" style={{ color:'var(--text-2)' }}>{desc}</p>
                <ul className="flex flex-col gap-[7px] mb-7">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-[9px] text-[0.8rem]" style={{ color:'var(--text-2)' }}>
                      <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background:colorMap[color], boxShadow:`0 0 8px ${colorMap[color]}50` }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <a href={href}
                  className="inline-flex items-center gap-2 text-[0.85rem] font-semibold transition-[gap] duration-300 hover:gap-4 no-underline"
                  style={{ color:colorMap[color] }}>
                  Learn More
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </a>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="glass-card reveal" style={{ padding:'44px 48px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:32, flexWrap:'wrap' }}>
            <div>
              <h3 className="font-display font-bold text-[1.35rem] tracking-[-0.03em] mb-2">Ready to accelerate your materials research?</h3>
              <p className="text-[0.88rem] font-light max-w-[400px]" style={{ color:'var(--text-2)' }}>Join leading institutions already using D2S Lab to cut discovery time by 10×.</p>
            </div>
            <div className="flex gap-4 flex-wrap flex-shrink-0">
              <a href="/contact" className="btn btn--primary">Start a Collaboration</a>
              <a href="/rd"      className="btn btn--glass">View Research</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
